import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, 
  Users, 
  Search,
  MoreHorizontal,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

import { API_BASE_URL } from '../utils/api';
const API_URL = API_BASE_URL;

export default function AdminCompanies() {
  const { admin } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin-portal/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data.companies);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const updateCompanyPlan = async (companyId, plan) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin-portal/companies/${companyId}`, 
        { plan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Company plan updated');
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to update company');
    }
  };

  const filteredCompanies = companies.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.account_number?.includes(searchTerm)
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'pro': return 'bg-cyan-500';
      default: return 'bg-slate-500';
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Companies</h1>
          <p className="text-slate-400 mt-1">
            Manage all registered companies
          </p>
        </div>
        <Button variant="outline" onClick={fetchCompanies} className="border-slate-600 text-slate-300 hover:bg-slate-700">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Search companies..."
            className="pl-10 bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Companies Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">All Companies ({total})</CardTitle>
          <CardDescription className="text-slate-400">
            View and manage company subscriptions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredCompanies.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-300">Company</TableHead>
                  <TableHead className="text-slate-300">Account #</TableHead>
                  <TableHead className="text-slate-300">Plan</TableHead>
                  <TableHead className="text-slate-300">Users</TableHead>
                  <TableHead className="text-slate-300">Status</TableHead>
                  <TableHead className="text-slate-300">Created</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCompanies.map((company) => (
                  <TableRow key={company.id} className="border-slate-700">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-blue-400" />
                        </div>
                        <span className="font-medium text-white">{company.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-slate-300">{company.account_number}</TableCell>
                    <TableCell>
                      <Select 
                        value={company.plan || 'free'} 
                        onValueChange={(value) => updateCompanyPlan(company.id, value)}
                      >
                        <SelectTrigger className="w-32 bg-slate-700 border-slate-600 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="free">Free</SelectItem>
                          <SelectItem value="pro">Pro</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-300">
                        <Users className="w-4 h-4 text-slate-400" />
                        {company.user_count}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        className={company.subscription_status === 'active' ? 'bg-green-500' : 'bg-slate-500'}
                      >
                        {company.subscription_status || 'trial'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-400">
                      {formatDate(company.created_at)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                          <DropdownMenuItem 
                            onClick={() => updateCompanyPlan(company.id, 'pro')}
                            className="text-slate-300 focus:bg-slate-700"
                          >
                            Upgrade to Pro
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 mx-auto text-slate-600 mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No companies found</h3>
              <p className="text-slate-400">Companies will appear here when users register</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
